import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import axios from 'axios';
import { Users, Copy, Gift, CheckCircle, Share2, Crown, Sparkles, MessageCircle, Twitter } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Referral = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);

  const fetchReferralInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/referral/info`);
      setReferralInfo(data);
    } catch (error) {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferralInfo();
  }, [fetchReferralInfo]);

  const shareUrl = referralInfo?.referral_code
    ? `https://aptivon.in/register?ref=${referralInfo.referral_code}`
    : '';

  const shareText = referralInfo?.referral_code
    ? `🎯 Hey! I'm crushing JEE/NEET mocks on Aptivon — AI-graded quizzes, weakness reports, and real cash rewards.\n\nSign up with my code *${referralInfo.referral_code}* and you'll get a +${referralInfo.new_user_bonus || 50} points head start. 🔥\n\n👉 ${shareUrl}`
    : '';

  const handleCopyCode = () => {
    if (referralInfo?.referral_code) {
      navigator.clipboard.writeText(referralInfo.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    toast.success('Invite link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const openWhatsApp = () => {
    if (!shareText) return;
    const wa = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(wa, '_blank', 'noopener,noreferrer');
  };

  const openTelegram = () => {
    if (!shareUrl) return;
    const tg = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(tg, '_blank', 'noopener,noreferrer');
  };

  const openTwitter = () => {
    if (!shareText) return;
    const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(tw, '_blank', 'noopener,noreferrer');
  };

  const nativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: 'Join Aptivon', text: shareText, url: shareUrl }).catch(() => {});
    } else {
      handleCopyLink();
    }
  };

  const handleApplyCode = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/referral/apply`, { referral_code: referralCode });
      toast.success(data.message);
      await refreshUser();
      await fetchReferralInfo();
      setReferralCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply referral code');
    } finally {
      setApplying(false);
    }
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscriptions/trial/start`);
      toast.success(data.message || 'Your Pro trial is live!');
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
        </div>
      </div>
    );
  }

  const count = referralInfo?.referral_count || 0;
  const threshold = referralInfo?.trial_threshold || 3;
  const friendsLeft = referralInfo?.friends_needed_for_trial ?? Math.max(0, threshold - count);
  const trialUnlocked = referralInfo?.trial_unlocked;
  const perReferral = referralInfo?.bonus_per_referral || 100;
  const newUserBonus = referralInfo?.new_user_bonus || 50;

  return (
    <div className="min-h-screen" data-testid="referral-page">
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] rounded-3xl flex items-center justify-center mx-auto mb-5 floating">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Invite friends. Unlock Pro.
          </h1>
          <p className="text-base text-[#6B7280] max-w-xl mx-auto">
            Earn <strong className="text-[#0EA5E9]">{perReferral} points</strong> per signup.
            Refer <strong className="text-amber-600">{threshold} friends</strong> → unlock <strong className="text-amber-600">7 days of Pro free</strong>.
          </p>
        </div>

        {/* Pro Trial Progress Wall */}
        <div className={`relative overflow-hidden rounded-3xl p-7 mb-8 ${trialUnlocked ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white' : 'bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] text-white'}`} data-testid="trial-progress-card">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/15 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-6 h-6 text-amber-300" />
              <p className="text-xs font-bold uppercase tracking-wider opacity-90">7-Day Pro Trial Challenge</p>
            </div>
            <p className="text-2xl sm:text-3xl font-black mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {trialUnlocked
                ? '🎉 You unlocked your Pro trial!'
                : friendsLeft === 0
                  ? 'Trial already claimed'
                  : `${friendsLeft} more friend${friendsLeft === 1 ? '' : 's'} to unlock`}
            </p>
            {/* Progress dots */}
            <div className="flex items-center gap-2 mb-5">
              {Array.from({ length: threshold }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-2.5 rounded-full transition-all ${i < count ? 'bg-amber-300' : 'bg-white/20'}`}
                />
              ))}
            </div>
            <p className="text-sm opacity-90 mb-5">
              {count} / {threshold} friends signed up using your code
            </p>
            {trialUnlocked ? (
              <button
                onClick={handleStartTrial}
                disabled={startingTrial}
                className="w-full sm:w-auto px-6 py-3.5 bg-white text-amber-600 rounded-2xl font-black shadow-lg hover:shadow-xl transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="claim-trial-button"
              >
                <Sparkles className="w-5 h-5" />
                {startingTrial ? 'Activating…' : 'Claim my 7-day Pro trial'}
              </button>
            ) : (
              <p className="text-xs opacity-80">
                Each signup also gives you +{perReferral} points instantly. Stack the wins.
              </p>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-8">
          <div className="quiz-card p-4 sm:p-6 text-center">
            <Users className="w-7 h-7 text-[#0EA5E9] mx-auto mb-2" />
            <p className="text-xs text-[#6B7280] mb-1">Friends</p>
            <p className="text-3xl sm:text-4xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {count}
            </p>
          </div>
          <div className="quiz-card p-4 sm:p-6 text-center">
            <Gift className="w-7 h-7 text-[#FBBF24] mx-auto mb-2" />
            <p className="text-xs text-[#6B7280] mb-1">Points earned</p>
            <p className="text-3xl sm:text-4xl font-black gradient-text" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {referralInfo?.total_bonus_earned || 0}
            </p>
          </div>
          <div className="quiz-card p-4 sm:p-6 text-center">
            <Sparkles className="w-7 h-7 text-[#10B981] mx-auto mb-2" />
            <p className="text-xs text-[#6B7280] mb-1">Per friend</p>
            <p className="text-3xl sm:text-4xl font-black text-[#10B981]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              +{perReferral}
            </p>
          </div>
        </div>

        {/* Your Code + Share */}
        <div className="quiz-card p-6 sm:p-8 mb-8">
          <h3 className="text-xl font-bold text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Your invite link
          </h3>

          <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-4">
            <div className="flex-1 p-3 bg-slate-50 border-2 border-dashed border-[#0EA5E9] rounded-2xl text-center">
              <p className="text-xs text-[#9CA3AF] mb-0.5">CODE</p>
              <span className="text-2xl sm:text-3xl font-black tracking-widest text-[#0EA5E9]" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="my-referral-code">
                {referralInfo?.referral_code || '---'}
              </span>
            </div>
            <button
              onClick={handleCopyCode}
              className={`px-5 py-3 rounded-2xl font-bold transition-all ${copied ? 'bg-[#10B981] text-white' : 'bg-[#0EA5E9] text-white hover:bg-[#0284C7]'}`}
              data-testid="copy-code-button"
            >
              {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>

          <div className="flex items-center gap-2 p-3 rounded-2xl bg-slate-50 border border-slate-200 mb-5 overflow-hidden">
            <span className="flex-1 truncate text-sm text-[#374151] font-mono" data-testid="invite-link">
              {shareUrl || '—'}
            </span>
            <button
              onClick={handleCopyLink}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition ${linkCopied ? 'bg-[#10B981] text-white' : 'bg-white border border-slate-300 text-[#0EA5E9] hover:bg-[#0EA5E9] hover:text-white'}`}
              data-testid="copy-link-button"
            >
              {linkCopied ? 'Copied!' : 'Copy link'}
            </button>
          </div>

          {/* Share targets */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              onClick={openWhatsApp}
              className="px-3 py-3 rounded-2xl font-bold bg-[#25D366] text-white shadow-[0_4px_0_rgb(18,140,82)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2 text-sm"
              data-testid="share-whatsapp-button"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </button>
            <button
              onClick={openTelegram}
              className="px-3 py-3 rounded-2xl font-bold bg-[#229ED9] text-white shadow-[0_4px_0_rgb(20,121,166)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2 text-sm"
              data-testid="share-telegram-button"
            >
              <Share2 className="w-4 h-4" /> Telegram
            </button>
            <button
              onClick={openTwitter}
              className="px-3 py-3 rounded-2xl font-bold bg-[#1DA1F2] text-white shadow-[0_4px_0_rgb(18,121,180)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2 text-sm"
              data-testid="share-twitter-button"
            >
              <Twitter className="w-4 h-4" /> X / Twitter
            </button>
            <button
              onClick={nativeShare}
              className="px-3 py-3 rounded-2xl font-bold bg-[#111827] text-white shadow-[0_4px_0_rgb(31,41,55)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2 text-sm"
              data-testid="share-more-button"
            >
              <Share2 className="w-4 h-4" /> More
            </button>
          </div>
        </div>

        {/* Apply a code */}
        <div className="quiz-card p-6 sm:p-8 mb-8">
          <h3 className="text-xl font-bold text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Have a referral code?
          </h3>
          <p className="text-sm text-[#6B7280] mb-4">
            Enter a friend's code to grab {newUserBonus} bonus points.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="flex-1 px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors uppercase tracking-widest font-bold text-center"
              placeholder="Enter code"
              data-testid="referral-code-input"
            />
            <button
              onClick={handleApplyCode}
              disabled={applying}
              className="px-6 py-3 bg-[#FBBF24] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(245,158,11)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
              data-testid="apply-code-button"
            >
              {applying ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </div>

        {/* How it works */}
        <h3 className="text-2xl font-bold text-[#111827] mb-5 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          How it works
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { color: 'bg-[#0EA5E9]', n: '1', t: 'Share your link', d: 'Drop it in WhatsApp groups, Insta DMs, Telegram channels.' },
            { color: 'bg-[#FBBF24]', n: '2', t: 'Friends sign up', d: `Each sign-up auto-applies your code. They get +${newUserBonus} pts.` },
            { color: 'bg-amber-500', n: '3', t: 'Unlock Pro free', d: `${threshold} sign-ups → 7-day Pro trial + ${perReferral * threshold} bonus pts.` },
          ].map((s) => (
            <div key={s.n} className="text-center">
              <div className={`w-12 h-12 ${s.color} rounded-2xl flex items-center justify-center mx-auto mb-3`}>
                <span className="text-white font-black text-lg">{s.n}</span>
              </div>
              <p className="font-bold text-[#111827] mb-1">{s.t}</p>
              <p className="text-sm text-[#6B7280]">{s.d}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Referral;
